import type { LucideIcon } from 'lucide-preact'
import type { TargetedPointerEvent } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import type { TouchButtonAction, VirtualInput } from '#contract'
import styles from './touch-button.module.css'

interface TouchButtonProps {
  readonly action: TouchButtonAction
  readonly icon: LucideIcon
  /** The accessible name; the button shows only its icon. */
  readonly label: string
  /** Positions the button. */
  readonly class?: string | undefined
  readonly touch: VirtualInput
}

/** Holds `action` down while a finger is on it. */
export function TouchButton({
  action,
  icon: Icon,
  label,
  class: className,
  touch,
}: TouchButtonProps) {
  const [pressed, setPressed] = useState(false)

  // Hidden while held (a pause, another device): release it.
  useEffect(
    () => () => {
      touch.setButton(action, false)
    },
    [touch, action],
  )

  const set = (down: boolean): void => {
    setPressed(down)
    touch.setButton(action, down)
  }

  return (
    <button
      type="button"
      // Touch only: it never takes focus from the menus' keyboard and gamepad navigation.
      tabIndex={-1}
      class={[styles.button, className].join(' ')}
      aria-label={label}
      data-pressed={pressed || undefined}
      onPointerDown={(event: TargetedPointerEvent<HTMLButtonElement>) => {
        event.currentTarget.setPointerCapture(event.pointerId)
        set(true)
      }}
      onPointerUp={() => {
        set(false)
      }}
      onPointerCancel={() => {
        set(false)
      }}
    >
      <Icon class={styles.icon} aria-hidden="true" />
    </button>
  )
}
